/**
 * Bridge Components for Hotwire Native communication
 * 
 * These components facilitate communication between the React web app
 * and native iOS components in the Hotwire Native wrapper.
 */

import { BridgeComponent } from '@hotwired/hotwire-native-bridge';

/**
 * Menu Bridge Component
 * Sends menu/action sheet data to native iOS UIMenu
 */
class MenuComponent extends BridgeComponent {
  static component = 'menu';
  
  static targets = ['item'];
  
  connect(): void {
    super.connect();
    
    const items = this.getMenuItems();
    this.send('connect', { items }, () => {
      // Callback when native acknowledges
      console.log('Menu bridge connected');
    });
  }
  
  private getMenuItems(): Array<{ title: string; action: string; destructive?: boolean }> {
    const items: Array<{ title: string; action: string; destructive?: boolean }> = [];
    
    // @ts-expect-error - targets are set by Stimulus
    this.itemTargets.forEach((item: HTMLElement) => {
      items.push({
        title: item.dataset.title || '',
        action: item.dataset.action || '',
        destructive: item.dataset.destructive === 'true',
      });
    });
    
    return items;
  }
  
  // Called by native app when menu item is selected
  performAction(action: string): void {
    const event = new CustomEvent('native-menu-action', {
      detail: { action },
      bubbles: true,
    });
    this.element.dispatchEvent(event);
  }
}

/**
 * Form Bridge Component
 * Provides native form submit button in iOS navigation bar
 */
class FormComponent extends BridgeComponent {
  static component = 'form';
  
  connect(): void {
    super.connect();
    
    const enabled = !this.isFormInvalid();
    this.send('connect', { enabled });
  }
  
  // Update button state when form validity changes
  update(): void {
    const enabled = !this.isFormInvalid();
    this.send('update', { enabled });
  }
  
  // Called by native app when submit button is tapped
  submitForm(): void {
    const form = this.element as HTMLFormElement;
    
    if (form.requestSubmit) {
      form.requestSubmit();
    } else {
      form.submit();
    }
  }
  
  private isFormInvalid(): boolean {
    const form = this.element as HTMLFormElement;
    return !form.checkValidity();
  }
}

/**
 * Share Bridge Component
 * Triggers native iOS share sheet
 */
class ShareComponent extends BridgeComponent {
  static component = 'share';
  
  share(): void {
    const element = this.element as HTMLElement;
    const title = element.dataset.shareTitle || '';
    const text = element.dataset.shareText || '';
    const url = element.dataset.shareUrl || window.location.href;
    
    this.send('share', { title, text, url });
  }
}

/**
 * Confirm Bridge Component  
 * Shows native iOS confirmation dialog
 */
class ConfirmComponent extends BridgeComponent {
  static component = 'confirm';
  
  show(): void {
    const element = this.element as HTMLElement;
    const title = element.dataset.confirmTitle || 'Confirm';
    const message = element.dataset.confirmMessage || 'Are you sure?';
    const confirmText = element.dataset.confirmButton || 'Confirm';
    const cancelText = element.dataset.cancelButton || 'Cancel';
    const destructive = element.dataset.destructive === 'true';
    
    this.send(
      'show',
      {
        title,
        message,
        confirmText,
        cancelText,
        destructive,
      },
      (message: unknown) => {
        if (typeof message === 'object' && message !== null && 'confirmed' in message) {
          const response = message as { confirmed: boolean };
          if (response.confirmed) {
            this.onConfirm();
          } else {
            this.onCancel();
          }
        }
      }
    );
  }
  
  private onConfirm(): void {
    const event = new CustomEvent('native-confirm', {
      detail: { confirmed: true },
      bubbles: true,
    });
    this.element.dispatchEvent(event);
  }
  
  private onCancel(): void {
    const event = new CustomEvent('native-confirm', {
      detail: { confirmed: false },
      bubbles: true,
    });
    this.element.dispatchEvent(event);
  }
}

/**
 * Navigation Bridge Component
 * Sends page title and navigation buttons to native iOS navigation bar
 */
class NavigationComponent extends BridgeComponent {
  static component = 'navigation';
  
  connect(): void {
    super.connect();
    this.updateNavigationBar();
    
    // Listen for custom update events from React
    this.element.addEventListener('navigation:update', this.handleNavigationUpdate.bind(this));
  }
  
  disconnect(): void {
    this.element.removeEventListener('navigation:update', this.handleNavigationUpdate.bind(this));
    super.disconnect();
  }
  
  // Handle custom update events from React
  private handleNavigationUpdate(): void {
    this.update();
  }
  
  // Update navigation bar when title or buttons change
  update(): void {
    this.updateNavigationBar();
  }
  
  private updateNavigationBar(): void {
    const element = this.element as HTMLElement;
    const title = element.dataset.navigationTitle || document.title;
    const showMenu = element.dataset.navigationShowMenu === 'true';
    const showSearch = element.dataset.navigationShowSearch === 'true';
    const showProfile = element.dataset.navigationShowProfile === 'true';
    
    this.send('update', {
      title,
      showMenu,
      showSearch,
      showProfile
    });
  }
  
  // Called by native when menu button is tapped
  menuButtonTapped(): void {
    const event = new CustomEvent('native-menu-button', {
      bubbles: true,
    });
    this.element.dispatchEvent(event);
  }
  
  // Called by native when search button is tapped
  searchButtonTapped(): void {
    const event = new CustomEvent('native-search-button', {
      bubbles: true,
    });
    this.element.dispatchEvent(event);
  }
  
  // Called by native when profile button is tapped
  profileButtonTapped(): void {
    const event = new CustomEvent('native-profile-button', {
      bubbles: true,
    });
    this.element.dispatchEvent(event);
  }
}

/**
 * Register all bridge components
 * Call this from main.tsx after Turbo.start()
 */
export function registerBridgeComponents(): void {
  if (!window.Stimulus) {
    console.warn('Stimulus not available, cannot register bridge components');
    return;
  }
  
  window.Stimulus.register('menu', MenuComponent);
  window.Stimulus.register('form', FormComponent);
  window.Stimulus.register('share', ShareComponent);
  window.Stimulus.register('confirm', ConfirmComponent);
  window.Stimulus.register('navigation', NavigationComponent);
  
  console.log('✅ Bridge components registered');
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    Stimulus?: {
      register(name: string, component: typeof BridgeComponent): void;
    };
  }
}
